/** Hosts that serve the SPA but nginx rejects POST /api (405 HTML). */
export function isStaticOnlyLoginHost(hostname?: string): boolean {
  const host = String(
    hostname || (typeof window !== 'undefined' ? window.location.hostname : '')
  )
    .trim()
    .toLowerCase();
  if (!host) return false;
  // Working full-stack host for this deploy is mw.yrgel.com
  if (host === 'mw.yrgel.com') return false;
  return host === 'www.yrgel.com' || host === 'yrgel.com';
}

export const WORKING_LOGIN_ORIGIN = 'https://mw.yrgel.com';

/**
 * When nginx returns 405/HTML for /api POSTs (common on www.* static hosts),
 * or the browser surfaces CORS/Network Error because OPTIONS/POST was blocked,
 * return a clear action instead of a generic "Network Error".
 */
export function apiHostMismatchMessage(error: any): string | null {
  const status = error?.response?.status ?? error?.status;
  // 413 body-too-large HTML is a size limit, not a wrong-host API block.
  if (status === 413) return null;

  const data = error?.response?.data;
  const looksHtml =
    typeof data === 'string' && /<\s*html[\s>]/i.test(data.trim());
  const looks405Html =
    looksHtml && /405|not allowed|method not allowed/i.test(String(data));
  const isNetwork =
    error?.code === 'ERR_NETWORK' ||
    /^network error$/i.test(String(error?.message || '').trim());

  const blockedByStatus = status === 405 || status === 404 || looks405Html;
  if (!blockedByStatus && !(isNetwork && isStaticOnlyLoginHost())) {
    return null;
  }

  if (isStaticOnlyLoginHost()) {
    const host =
      typeof window !== 'undefined' ? window.location.host : 'www';
    return `This site (${host}) does not accept API requests (nginx blocks POST /api). Open ${WORKING_LOGIN_ORIGIN} for login, photo uploads, and admin tools.`;
  }
  if (status === 405 || looks405Html) {
    return 'API is not available on this host (request blocked). Use the MakerSet API host URL (e.g. mw.yrgel.com).';
  }
  return null;
}

/**
 * Nginx 413 (body too large) often has no CORS headers, so the browser reports
 * only axios ERR_NETWORK / "Network Error" — especially for camera JPGs > ~1MB.
 * Pass `file` when available so Network Error + large local file can be explained.
 */
export function uploadTooLargeMessage(error: any, file?: File | null): string | null {
  if (isStaticOnlyLoginHost()) return null;

  const status = error?.response?.status ?? error?.status;
  const data = error?.response?.data;
  const bodyText = typeof data === 'string' ? data : '';
  const looks413 =
    status === 413 ||
    /413|request entity too large/i.test(bodyText) ||
    /413|request entity too large/i.test(String(error?.message || ''));

  const isNetwork =
    error?.code === 'ERR_NETWORK' ||
    /^network error$/i.test(String(error?.message || '').trim());
  const largeFile = Boolean(file && file.size > 900 * 1024);

  if (!looks413 && !(isNetwork && largeFile)) {
    return null;
  }

  const sizeHint = file
    ? ` Selected file is about ${(file.size / (1024 * 1024)).toFixed(1)}MB.`
    : '';
  return (
    `Upload was blocked by the reverse proxy (file too large; limit is about 1MB).${sizeHint} ` +
    `The app will try to shrink photos before upload; if this persists, raise nginx client_max_body_size on the API host.`
  );
}

/**
 * Turn any thrown/API error payload into a human-readable string.
 * Prevents React from rendering "[object Object]" when backends return
 * `{ error: { message, statusCode, path } }` (global 404/500 shape).
 * Prefers API response bodies over axios's generic
 * "Request failed with status code N" message.
 */
export function toErrorMessage(
  error: any,
  fallback = 'An error occurred',
  options?: { file?: File | null }
): string {
  const hostHint = apiHostMismatchMessage(error);
  if (hostHint) return hostHint;

  const sizeHint = uploadTooLargeMessage(error, options?.file);
  if (sizeHint) return sizeHint;

  const statusEarly = error?.response?.status ?? error?.status;
  if (statusEarly === 429) {
    return (
      'Too many requests from this network right now. Wait a minute and try again, ' +
      'or refresh the page. Photo uploads should not be blocked by browsing the gallery.'
    );
  }

  const seen = new Set<any>();

  const isAxiosStatusMessage = (text: string) =>
    /^request failed with status code \d+/i.test(text);

  const unwrap = (value: any, depth = 0): string | null => {
    if (value == null) return null;
    if (typeof value === 'string') {
      const trimmed = value.trim();
      if (!trimmed || trimmed === '[object Object]') return null;
      if (isAxiosStatusMessage(trimmed)) return null;
      // Never surface raw nginx HTML error pages in the UI
      if (/<\s*html[\s>]/i.test(trimmed)) return null;
      return trimmed;
    }
    if (typeof value === 'number' || typeof value === 'boolean') {
      return String(value);
    }
    if (depth > 4 || (typeof value === 'object' && seen.has(value))) {
      return null;
    }
    if (typeof value === 'object') {
      seen.add(value);
      // Prefer structured API payloads over Error.message / axios defaults.
      return (
        unwrap(value.response?.data?.error, depth + 1) ||
        unwrap(value.response?.data?.message, depth + 1) ||
        unwrap(value.response?.data?.details, depth + 1) ||
        unwrap(value.response?.data, depth + 1) ||
        unwrap(value.error, depth + 1) ||
        unwrap(value.details, depth + 1) ||
        unwrap(value.message, depth + 1)
      );
    }
    return null;
  };

  const status = error?.response?.status;
  const msg = unwrap(error);
  if (msg) return msg;
  if (typeof status === 'number') {
    return `Request failed with status code ${status}`;
  }
  return fallback;
}

/** Safely render error messages for Alert / Typography children. */
export const renderError = (error: any): string => toErrorMessage(error);

/** React helper to safely render errors. */
export const SafeErrorDisplay: React.FC<{ error: any }> = ({ error }) => {
  return <>{renderError(error)}</>;
};
